import {
    Button,
    Checkbox,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { useCallback, useEffect, useState, type ChangeEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as API from '../../API';
import type IdType from '../../Models/IdType';
import type PhysicianIDType from '../../Models/PhysicianIDType';

import Styles from './PhysicianIdentifiers.module.scss';

enum AddResult {
    Success,
    AlreadyExist,
    Error,
}

const commonCellSx = {
    paddingTop: '5px',
    paddingBottom: '5px',
};

function PhysicianIdentifiers({
    physicianID,
    isEdit,
}: Readonly<{
    physicianID: number;
    isEdit: boolean;
}>): React.ReactElement {
    const { t } = useTranslation();

    const [rizivFormatting, setRIZIVFormatting] = useState('');
    const [customIds, setCustomIds] = useState<PhysicianIDType[]>([]);
    const [identifierTypes, setIdentifierTypes] = useState<IdType[]>([]);

    const uniquePrefixElement = document.getElementById('uniquePrefix');
    const uniquePrefix = uniquePrefixElement instanceof HTMLInputElement ? uniquePrefixElement.value : '';

    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editedRow, setEditedRow] = useState({ Name: '', Value: '', UseFormatting: false, Type_UID: 0 });

    useEffect(() => {
        getRIZIVFormatting();
        getPhysiciansIds();
        getIdentifierTypes();
    }, []);

    const setIdentifiersFromSession = (res: PhysicianIDType[]): void => {
        const storedData = sessionStorage.getItem(`IdentifierModel${uniquePrefix}`);
        const identifierModel: PhysicianIDType[] = storedData ? JSON.parse(storedData) : [];

        const updatedIdentifiers = storedData ? identifierModel : res;
        const updatedRes = [...updatedIdentifiers];
        setCustomIds(updatedRes);
    };

    const getPhysiciansIds = (): void => {
        API.physicians
            .getPhysicianIDs(physicianID)
            .then((res) => {
                setIdentifiersFromSession(res);
            })
            .catch((err) => {
                console.error('Could not get ids:', err);
            });
    };

    const getIdentifierTypes = (): void => {
        API.identifiers
            .getIdentifierTypes(1)
            .then((res: { identifierTypes: IdType[] }) => {
                setIdentifierTypes(res.identifierTypes);
            })
            .catch((err) => {
                console.error('Error fetching identifier types:', err);
            });
    };

    const getRIZIVFormatting = (): void => {
        // NOTE: This is necessary because we can't use the SystemSettingsContext because this component is a library component still. This should be removed in favor of the context eventually
        API.general
            .getGeneralSettings()
            .then((res) => {
                if (res == null) {
                    return;
                }
                setRIZIVFormatting(res.settings.ValidateRIZIV.value);
            })
            .catch((err) => {
                console.error('Could not get general settings for RIZIV formatting:', err);
            });
    };

    const onAdd = (type: number, value: string, useFormatting: boolean): AddResult => {
        const selectedType = identifierTypes.find((x) => x.ID === type);
        if (selectedType === undefined) {
            console.error(`Type with ID ${type} not found`);
            return AddResult.Error;
        }
        const alreadyContainsType = customIds.some((id) => id.Name === selectedType.Name);
        if (alreadyContainsType) {
            return AddResult.AlreadyExist;
        }

        setCustomIds((prevState) => {
            const newState = [
                ...prevState,
                {
                    Type_UID: type,
                    Name: selectedType.Name,
                    TypeDescription: selectedType.Description,
                    Value: value,
                    UseFormatting: useFormatting,
                },
            ];
            if (uniquePrefix !== undefined) {
                sessionStorage.setItem('IdentifierModel' + uniquePrefix, JSON.stringify(newState));
            }
            return newState;
        });
        return AddResult.Success;
    };

    const onDelete = (index: number): void => {
        const customIdsAfterDeletion = customIds.filter((_, i) => i !== index);
        if (uniquePrefix !== undefined) {
            sessionStorage.setItem('IdentifierModel' + uniquePrefix, JSON.stringify(customIdsAfterDeletion));
        }
        setCustomIds(customIdsAfterDeletion);
    };

    const onEdit = (index: number): void => {
        setEditIndex(index);
        setEditedRow(customIds[index]);
    };

    const handleInputChange = (index: number, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setEditedRow((prevState) => {
            return { ...prevState, Value: e.target.value };
        });

        const newCustomIds = [...customIds];
        newCustomIds[index] = { ...newCustomIds[index], Value: e.target.value };

        setCustomIds(newCustomIds);
    };

    const handleCheckboxChange = (index: number, e: React.ChangeEvent<HTMLInputElement>): void => {
        const newCustomIds = [...customIds];
        newCustomIds[index] = { ...newCustomIds[index], UseFormatting: e.target.checked };
        setCustomIds(newCustomIds);
    };

    return (
        <div>
            <TableContainer component={Paper}>
                <Table id="identifiersTable">
                    <TableHead className={Styles.tableHeader}>
                        <TableRow>
                            <TableCell sx={{ ...commonCellSx, textAlign: 'left !important' }}>{t('Type')}</TableCell>
                            <TableCell sx={commonCellSx}>{t('Value')}</TableCell>
                            <TableCell sx={commonCellSx}>{t('UseFormatting')}</TableCell>
                            {isEdit && <TableCell sx={commonCellSx}>{t('Action')}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Check if customIds is empty */}
                        {customIds.length === 0
                            ? <NoIdentifiersRow isEdit={isEdit} />
                            : (
                                    customIds.map((row, index) => (
                                        <TableRow key={row.Type_UID}>
                                            <TableCell sx={commonCellSx}>{row.TypeDescription ?? row.Name}</TableCell>
                                            <TableCell sx={commonCellSx}>
                                                {editIndex === index ? <TextField value={editedRow.Value} variant="standard" onChange={(e) => handleInputChange(index, e)} /> : row.Value}
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabelCellContent editIndex={editIndex} index={index} editedRow={editedRow} row={row} handleCheckboxChange={handleCheckboxChange} />
                                            </TableCell>
                                            {isEdit && (
                                                <TableCell sx={commonCellSx}>
                                                    <Button onClick={() => onDelete(index)} variant="outlined" startIcon={<span className="material-symbols-outlined">delete</span>}>
                                                        {t('Remove')}
                                                    </Button>
                                                    <Button
                                                        style={{ marginLeft: '10px' }}
                                                        onClick={() => onEdit(index)}
                                                        variant="outlined"
                                                        startIcon={<span className="material-symbols-outlined">edit</span>}
                                                    >
                                                        {t('Edit')}
                                                    </Button>
                                                </TableCell>
                                            )}

                                            {/* Hidden fields that hold the data that will be sent with the form on Save (and Close) */}
                                            <TableCell style={{ display: 'none' }}>
                                                <input type="hidden" name={`IdentifierTypes[${index}].Type_UID`} value={row.Type_UID} />
                                                <input type="hidden" name={`IdentifierTypes[${index}].Name`} value={row.Name} />
                                            </TableCell>
                                            <TableCell style={{ display: 'none' }}>
                                                <input type="hidden" name={`IdentifierTypes[${index}].Value`} value={row.Value} />
                                            </TableCell>
                                            <TableCell style={{ display: 'none' }}>
                                                <input type="hidden" name={`IdentifierTypes[${index}].UseFormatting`} value={String(row.UseFormatting)} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                    </TableBody>
                </Table>
            </TableContainer>

            {isEdit ? <NewPhysicianIdentifier identifierTypes={identifierTypes} rizivFormatting={rizivFormatting} onAdd={onAdd} customIds={customIds} /> : null}
        </div>
    );
}

interface FormControlLabelCellContentProps {
    editIndex: number | null;
    index: number;
    editedRow: { Type_UID: number };
    row: { UseFormatting: boolean };
    handleCheckboxChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
}

function FormControlLabelCellContent({ editIndex, index, editedRow, row, handleCheckboxChange }: Readonly<FormControlLabelCellContentProps>): ReactNode {
    const { t } = useTranslation();

    if (editIndex !== index) {
        if (!row.UseFormatting) {
            return null;
        }
        return <>{t('Yes')}</>;
    }

    if (editedRow.Type_UID !== 1) {
        return null;
    }

    return <FormControlLabel control={<Checkbox checked={row.UseFormatting} onChange={(e) => handleCheckboxChange(index, e)} />} label={t('UseFormatting')} />;
}

function NoIdentifiersRow({ isEdit }: Readonly<{ isEdit: boolean }>): ReactElement {
    const { t } = useTranslation();

    return (
        <TableRow>
            <TableCell sx={commonCellSx} colSpan={isEdit ? 4 : 3} style={{ textAlign: 'center' }}>
                {t('NoIdentifiers')}
            </TableCell>
        </TableRow>
    );
}

function NewPhysicianIdentifier({ onAdd, rizivFormatting, identifierTypes, customIds }: Readonly<{
    onAdd: (type: number, value: string, useFormatting: boolean) => AddResult;
    rizivFormatting: string;
    identifierTypes: IdType[];
    customIds: PhysicianIDType[];
}>): React.ReactElement {
    const { t } = useTranslation();

    const [type, setType] = useState<number>(0);
    const previousInputValue = document.querySelector('.newIdentifierValueID')?.querySelector('input')?.value ?? '';
    const [value, setValue] = useState(previousInputValue ?? '');// this resets values on reload something like getdocumentbyid
    const [useFormatting, setUseFormatting] = useState(false);

    const [isValueValid, setIsValueValid] = useState(true);
    const [isTypeValid, setIsTypeValid] = useState(true);
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const [rizivFormat, setRizivFormat] = useState('');
    const [rizivFormatPlaceholder, setRizivFormatPlaceholder] = useState('');

    const isCustomIdPresent = (name: string): boolean => {
        return customIds.some((id) => id.Name === name);
    };

    const getRIZIVFormat = (): void => {
        API.identifiers.getRIZIVFormat().then((res) => {
            setRizivFormat(res.format);
        }).catch((err) => {
            console.error('Error fetching RIZIV format:', err);
        });
    };

    useEffect(() => {
        getRIZIVFormat();
    }, []);

    // NOTE: Functionality created as done in PhysicianHelper.GenerateRegexForIdentificationNumber
    const createRegex = useCallback((): string => {
        if (rizivFormatting === null || rizivFormatting === '') {
            return '^(.*)$';
        }

        let regex = '^';
        for (const c of rizivFormatting) {
            if (!Number.isNaN(+c)) {
                regex += '\\d';
            }
            else if (/[a-zA-Z]/.exec(c)) {
                regex += '\\w';
            }
            else {
                regex += c;
            }
        }
        regex += '$';
        return regex;
    }, [rizivFormatting]);

    const rizivRegex = new RegExp(createRegex());

    const validateFields = (): boolean => {
        setIsTypeValid(true);
        setIsValueValid(true);

        if (type === 0) {
            setIsTypeValid(false);
            setValidationErrorMessage(t('Required'));
            return false;
        }

        if (value === '' || value.trim() === '') {
            setIsValueValid(false);
            setValidationErrorMessage(t('Required'));
            return false;
        }

        if (type === 1 && useFormatting) {
            if (!rizivRegex.test(value)) {
                setIsTypeValid(false);
                const useFormat = t('Use') + ' ' + rizivFormat;
                setValidationErrorMessage(useFormat);
                return false;
            }
        }

        return true;
    };

    const handleChange = (event: SelectChangeEvent<string | number>): void => {
        const selectedValue = event.target.value as number; // Assuming event.target.value is already string
        const hasSelectedType = identifierTypes.some((type) => type.ID === selectedValue);
        if (!hasSelectedType) {
            console.error(`Type with ID ${selectedValue} not found`);
            return;
        }

        setType(selectedValue);
    };

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setValue(event.target.value.toString());
    };

    const handleIdentifierUseRIZIVFormat = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setUseFormatting(event.target.checked);
        setRizivFormatPlaceholder(event.target.checked ? rizivFormat : '');
    };

    const handleAdd = (): void => {
        if (!validateFields()) {
            return;
        }

        const result = onAdd(type, value, useFormatting);
        if (result === AddResult.AlreadyExist) {
            setIsTypeValid(false);
            setValidationErrorMessage(t('IdentifierTypeAlreadyExist'));
        }

        setType(0);
        setValue('');
        setUseFormatting(false);
    };

    return (
        <div className={Styles.add}>
            <FormControl variant="standard">
                <InputLabel id="demo-simple-select-standard-label">Identifier type</InputLabel>
                <Select
                    style={{ marginRight: '20px' }}
                    labelId="demo-simple-select-standard-label"
                    id="demo-simple-select-standard"
                    value={type}
                    label={t('Identifier')}
                    onChange={handleChange}
                >
                    <MenuItem disabled value="0">{t('SelectIdentifier')}</MenuItem>
                    {identifierTypes?.map((type) => {
                        const isDisabled = isCustomIdPresent(type.Name);
                        return (
                            <MenuItem
                                key={type.ID}
                                value={type.ID}
                                disabled={isDisabled}
                            >
                                {isDisabled ? `${type.Name} (${t('IdentifierTypeAlreadyExist')})` : type.Name}
                            </MenuItem>
                        );
                    })}
                </Select>
                {!isTypeValid && <FormHelperText className={Styles.validationError}>{validationErrorMessage}</FormHelperText>}
            </FormControl>
            <TextField
                className="newIdentifierValueID"
                error={!isValueValid}
                label={`${t('Value')} ${rizivFormatPlaceholder}`}
                value={value}
                helperText={!isValueValid ? validationErrorMessage : ''}
                variant="standard"
                style={{ marginRight: '10px' }}
                onChange={handleTextFieldChange}
            />
            {type === 1 ? <FormControlLabel control={<Checkbox onChange={handleIdentifierUseRIZIVFormat} />} label={t('UseFormatting')} /> : null}
            <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleAdd}
            >
                +
            </Button>
        </div>
    );
}

export default PhysicianIdentifiers;
